// The client's money across the matters the viewer can see, read from
// one endpoint and acted on with the same forms the matter card and the
// ledger use. Rows carry the matter's eId - on this page every bill
// belongs to the same person, so the case is the thing telling them apart.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useClientBilling } from "../hooks/useBilling";
import { downloadInvoicePdf } from "../api/caseInvoices";
import type { ClientInvoiceRow } from "../api/billing";
import { RecordPaymentForm, recordedNote } from "./RecordPaymentForm";
import { VoidInvoiceButton } from "./VoidInvoiceButton";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { useModuleOn } from "../../../api/attorneyConfig";
import { EntityLink } from "../../../components/chrome/EntityLink";
import { SkeletonCard } from "../../../components/chrome/Skeleton";
import { EmptyCopy } from "../../../components/chrome/EmptyCopy";
import { fmtCents } from "../../../lib/money";
import { BILLING_OFF_COPY, day, INVOICE_PILL } from "../lib/billingLabels";

// A live schedule is healthy, a finished or abandoned one is history.
const PLAN_PILL: Record<string, string> = {
  active: "ok",
  completed: "muted",
  cancelled: "muted",
};

export function ClientBillingTab({ clientId, fixtures }: { clientId: string; fixtures?: boolean }) {
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const billingOn = useModuleOn("billing");
  const q = useClientBilling(clientId, !fixtures);
  const [recordForId, setRecordForId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // One outcome line at a time: a fresh result replaces both, so a stale
  // error never sits above a new confirmation.
  const savePdf = async (inv: ClientInvoiceRow) => {
    setNote(null);
    setActionError(null);
    try {
      await downloadInvoicePdf(inv._id, inv.number);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not download the PDF.");
    }
  };

  if (fixtures) {
    return (
      <div className="client-tab-empty">
        This is prototype data - there is no billing behind it.
      </div>
    );
  }
  // Money never degrades into an empty state: "nothing billed" and "could
  // not ask" are different answers. Rows already on screen survive a
  // failed background refetch - dropping them would also drop the note
  // confirming a payment that did land.
  if (!q.data) {
    if (q.isError) {
      return (
        <div className="cc-card">
          <div className="cc-card-note bad" role="alert">
            Could not reach the server - billing for this client is unknown right now.
          </div>
        </div>
      );
    }
    return <SkeletonCard lines={3} />;
  }

  const { invoices, plans } = q.data;
  if (invoices.length === 0 && plans.length === 0) {
    return (
      <div className="cc-empty">
        <EmptyCopy
          title="Nothing billed for this client yet"
          mine="Nothing billed on the matters assigned to you"
          body={
            billingOn
              ? "Raise the first invoice from the New invoice button above - it finds the matter and emails the client if you ask it to."
              : BILLING_OFF_COPY
          }
        />
      </div>
    );
  }
  const slice = isAdmin ? "this client's matters" : "the matters you can see";

  return (
    <>
      {plans.length > 0 && (
        <div className="cc-card" style={{ marginBottom: 14 }}>
          <div className="cc-card-head">
            <div>
              <div className="cc-card-title">Payment plans</div>
              <div className="cc-card-sub">
                {plans.length} plan{plans.length === 1 ? "" : "s"} across {slice}
              </div>
            </div>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {plans.map((p) => (
              <li key={p._id} className="cc-money-row cc-money-cols-client-plan">
                <span className="amt">{fmtCents(p.totalCents)}</span>
                <span style={{ fontSize: 13 }}>
                  {p.caseEId && (
                    <>
                      <EntityLink kind="matter" id={p.caseEId} eID={p.caseEId} subtle>
                        <span className="tnum-mono">{p.caseEId}</span>
                      </EntityLink>
                      {" - "}
                    </>
                  )}
                  {p.paidCount} of {p.installmentCount} paid
                  {p.nextDueDate && (
                    <>
                      {", next "}
                      <span className="tnum">{fmtCents(p.nextAmountCents ?? 0)}</span>
                      {" due "}
                      <span className="tnum">{p.nextDueDate}</span>
                    </>
                  )}
                </span>
                <span className={`cc-pill ${PLAN_PILL[p.status] ?? "muted"}`}>{p.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="cc-card">
        <div className="cc-card-head">
          <div>
            <div className="cc-card-title">Invoices</div>
            <div className="cc-card-sub">
              {invoices.length} invoice{invoices.length === 1 ? "" : "s"} across {slice}
            </div>
          </div>
        </div>

        {q.isError && (
          <div className="cc-card-note bad" role="alert">
            Could not refresh - showing the last loaded state.
          </div>
        )}
        {note && <div className="cc-card-note">{note}</div>}
        {actionError && (
          <div role="alert" className="cc-card-note bad">
            {actionError}
          </div>
        )}

        {invoices.length === 0 && (
          <div style={{ padding: "8px 18px", color: "var(--muted)", fontSize: 13 }}>
            No invoices yet - only a payment plan waiting to issue its first part.
          </div>
        )}

        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {invoices.map((inv) => (
            <li key={inv._id}>
              <div className="cc-money-row cc-money-cols-client">
                <span className="amt">{fmtCents(inv.amountCents)}</span>
                <span style={{ fontSize: 13 }}>
                  <Link className="cc-link-subtle" to={`/billing/invoice/${inv._id}`}>
                    <span className="tnum-mono">{inv.number ?? ""}</span>
                    {inv.number ? " " : ""}
                    {inv.description}
                  </Link>
                  {inv.dueDate && (
                    <>
                      {" (due "}
                      <span className="tnum">{day(inv.dueDate)}</span>
                      {")"}
                    </>
                  )}
                  {inv.caseEId && (
                    <>
                      {" - "}
                      <EntityLink kind="matter" id={inv.caseEId} eID={inv.caseEId} subtle>
                        <span className="tnum-mono">{inv.caseEId}</span>
                      </EntityLink>
                    </>
                  )}
                </span>
                <span className={`cc-pill ${INVOICE_PILL[inv.status]}`}>{inv.status}</span>
                <span className="actions">
                  <button type="button" className="cc-link-btn" onClick={() => void savePdf(inv)}>
                    PDF
                  </button>
                  {inv.status === "open" && isAdmin && billingOn && (
                    <button
                      type="button"
                      className="cc-link-btn"
                      onClick={() => setRecordForId((v) => (v === inv._id ? null : inv._id))}
                    >
                      {recordForId === inv._id ? "Close form" : "Record payment"}
                    </button>
                  )}
                  {inv.status === "open" && billingOn && (
                    <VoidInvoiceButton
                      invoiceId={inv._id}
                      caseId={inv.caseId}
                      number={inv.number}
                      amountCents={inv.amountCents}
                      onVoided={() => {
                        setNote(null);
                        setActionError(null);
                      }}
                      onError={setActionError}
                    />
                  )}
                </span>
              </div>
              {billingOn && recordForId === inv._id && inv.status === "open" && (
                <RecordPaymentForm
                  invoiceId={inv._id}
                  caseId={inv.caseId}
                  number={inv.number}
                  amountCents={inv.amountCents}
                  onCancel={() => setRecordForId(null)}
                  onRecorded={(healed) => {
                    setRecordForId(null);
                    setActionError(null);
                    setNote(recordedNote(healed, inv.number, inv.amountCents));
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
